import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const MeditationGame = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(60); // default 60 seconds
  const [timeLeft, setTimeLeft] = useState(60);
  const [stage, setStage] = useState("Get Comfortable 🧘‍♂");
  const [mute, setMute] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const audioRef = useRef(null);

  const stages = [
    "Get Comfortable 🧘‍♀",
    "Focus on Your Breath 🌬",
    "Relax Your Shoulders 💆",
    "Observe Thoughts Without Judgment ☁️",
    "Feel the Present Moment 🌿",
    "You Did Beautifully ✨",
  ];

  // Update timer
  useEffect(() => {
    let interval;
    if (isPlaying && timeLeft > 0) {
      interval = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    } else if (timeLeft === 0) {
      clearInterval(interval);
      setStage("You Did Beautifully ✨");
      setIsPlaying(false);
      audioRef.current.pause();
    }
    return () => clearInterval(interval);
  }, [isPlaying, timeLeft]);

  // Update stage depending on time
  useEffect(() => {
    const stageIndex = Math.floor(((duration - timeLeft) / duration) * stages.length);
    if (stageIndex < stages.length) setStage(stages[stageIndex]);
  }, [timeLeft, duration]);

  const handleStart = () => {
    if (!startTime || !duration) {
      alert("Please set both start time and duration ⏰");
      return;
    }

    const start = new Date();
    const [hours, minutes] = startTime.split(":").map(Number);
    start.setHours(hours);
    start.setMinutes(minutes);

    const end = new Date(start.getTime() + duration * 1000);
    const endHours = end.getHours().toString().padStart(2, "0");
    const endMinutes = end.getMinutes().toString().padStart(2, "0");
    setEndTime(`${endHours}:${endMinutes}`);

    setTimeLeft(duration);
    setStage("Get Comfortable 🧘‍♂");
    setIsPlaying(true);
    audioRef.current.play().catch(() => {});
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col justify-center items-center text-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #A8C5FF, #D7E7FF, #F4F9FF)",
      }}
    >
      {/* Background Music */}
      <audio ref={audioRef} src="/wildflowers.mp3" loop />

      {/* Music Toggle Button */}
      <button
        onClick={() => {
          mute ? audioRef.current.play() : audioRef.current.pause();
          setMute(!mute);
        }}
        className="absolute top-4 right-4 px-4 py-2 bg-white shadow rounded-lg text-sm"
      >
        {mute ? "🔊 Music On" : "🔇 Mute"}
      </button>

      {/* Title */}
      <motion.h1
        className="text-5xl font-extrabold mb-4 text-indigo-700 drop-shadow-sm"
        initial={{ opacity: 0, y: -25 }}
        animate={{ opacity: 1, y: 0 }}
      >
        Calm Breathing Meditation
      </motion.h1>

      {/* Subtext */}
      <motion.p
        className="text-lg text-gray-700 mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        Breathe in. Breathe out. Everything is okay
      </motion.p>

      {!isPlaying ? (
        <motion.div
          className="p-8 rounded-2xl bg-white/40 backdrop-blur-xl shadow-xl border border-white/60 max-w-sm"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <p className="text-gray-700 text-lg mb-4">
            Set your meditation time and begin your journey 🕊️
          </p>

          {/* Start Time Input */}
          <div className="mb-4">
            <label className="block mb-1 text-gray-800 font-semibold">
              Start Time ⏰
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 shadow-sm focus:ring focus:ring-indigo-200"
            />
          </div>

          {/* Duration Input */}
          <div className="mb-4">
            <label className="block mb-1 text-gray-800 font-semibold">
              Session Duration (minutes) ⏳
            </label>
            <input
              type="number"
              min="1"
              max="120"
              placeholder="e.g. 10"
              onChange={(e) => setDuration(Number(e.target.value) * 60)}
              className="px-4 py-2 rounded-lg border border-gray-300 shadow-sm focus:ring focus:ring-indigo-200"
            />
          </div>

          {/* Start Button */}
          <motion.button
            onClick={handleStart}
            className="px-8 py-4 text-lg rounded-xl font-semibold bg-indigo-600 text-white shadow-lg hover:bg-indigo-700"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Meditation
          </motion.button>
        </motion.div>
      ) : (
        <>
          {/* Timer Circle */}
          <motion.div
            className="relative flex items-center justify-center w-56 h-56 rounded-full border-8 border-indigo-300 shadow-xl bg-white/40 backdrop-blur-md"
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.9, 1, 0.9],
            }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            <span className="text-4xl font-bold text-indigo-700">
              {Math.floor(timeLeft / 60)}:
              {(timeLeft % 60).toString().padStart(2, "0")}
            </span>
          </motion.div>

          {/* Stage Text */}
          <motion.p
            className="mt-8 text-2xl font-medium text-indigo-800"
            key={stage}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {stage}
          </motion.p>

          {/* Start/End Info */}
          <p className="mt-4 text-gray-700 text-lg">
            🕓 Start: {startTime || "--:--"} | 🕕 End: {endTime || "--:--"}
          </p>

          {/* End Button */}
          <motion.button
            onClick={() => {
              setIsPlaying(false);
              audioRef.current.pause();
            }}
            className="mt-8 px-6 py-3 bg-gray-800 text-white rounded-lg shadow hover:bg-black/80 transition-all"
            whileHover={{ scale: 1.07 }}
          >
            End Session
          </motion.button>
        </>
      )}
    </div>
  );
};

export default MeditationGame;
